"use client";

import { useState, type ReactNode } from "react";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { Typography } from "./Typography";
import { Button } from "@/components/forms/Button";
import { cn } from "@/lib/utils/cn";

export interface AccordionProps {
  title: string;
  subtitle?: ReactNode;
  icon?: LucideIcon;
  numberBadge?: number;
  badge?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  variant?: "default" | "colored" | "subtle" | "surface";
  size?: "default" | "sm";
  className?: string;
  headerClassName?: string;
}

export function Accordion({
  title,
  subtitle,
  icon: Icon,
  numberBadge,
  badge,
  children,
  defaultOpen = false,
  variant = "default",
  size = "default",
  className = "",
  headerClassName = "",
}: AccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const variantStyles = {
    default: {
      header: "hover:bg-gray-100",
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      numberBg: "bg-gray-100",
      numberText: "text-gray-700",
    },
    colored: {
      header: "bg-blue-50 hover:bg-blue-100",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-700",
      numberBg: "bg-blue-100",
      numberText: "text-blue-700",
    },
    subtle: {
      header: "bg-gray-50 hover:bg-gray-100",
      iconBg: "bg-gray-100",
      iconColor: "text-gray-600",
      numberBg: "bg-gray-200",
      numberText: "text-gray-700",
    },
    surface: {
      header: "bg-white hover:bg-gray-50",
      iconBg: "bg-transparent",
      iconColor: "text-gray-500",
      numberBg: "bg-gray-50",
      numberText: "text-gray-600",
    },
  };

  const styles = variantStyles[variant];
  const isSurface = variant === "surface";

  return (
    <div
      data-variant={variant}
      className={cn(
        "overflow-hidden border border-gray-200 bg-white",
        isSurface ? "rounded-lg shadow-none" : "rounded-2xl shadow-sm",
        className,
      )}
    >
      {/* Header */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={cn("w-full px-4 py-4 flex items-center justify-between transition-colors cursor-pointer", styles.header, headerClassName)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {Icon && (
            <div className={cn(styles.iconBg, isSurface ? "p-0" : "p-3 rounded-xl", "flex-shrink-0")}>
              <Icon className={cn(isSurface ? "h-4 w-4" : "h-5 w-5", styles.iconColor)} />
            </div>
          )}
          {numberBadge !== undefined && (
            <div
              className={cn(styles.numberBg, "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0")}
            >
              <Typography as="span" variant="body" weight="semibold" color={styles.numberText}>
                {numberBadge}
              </Typography>
            </div>
          )}
          <div className="flex-1 min-w-0 text-left">
            <Typography
              variant={isSurface ? "overline" : size === "sm" ? "body" : "headline5"}
              as="div"
              weight="semibold"
              truncate
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant={isSurface ? "caption" : "body"}
                color="muted"
                as="div"
                className={isSurface ? "mt-0.5" : "mt-1"}
              >
                {subtitle}
              </Typography>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {badge}
          <Button
            type="button"
            iconOnly
            startIcon={<ChevronDown className={cn("w-4 h-4 text-gray-600 transition-transform", isOpen ? "rotate-180" : "")} />}
            variant="ghost"
            size={isSurface ? "sm" : "md"}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            title={isOpen ? "Collapse" : "Expand"}
            aria-label={isOpen ? "Collapse section" : "Expand section"}
            className={isSurface
              ? "border-transparent bg-transparent text-gray-600 shadow-none hover:border-gray-200 hover:bg-gray-50"
              : "bg-blue-100 border border-blue-200 shadow-sm hover:bg-blue-200 hover:border-blue-300"}
          />
        </div>
      </div>

      {/* Content */}
      {isOpen && (
        <div className={cn("px-4 pb-4 pt-4", isSurface && "border-t border-gray-100")}>
          {children}
        </div>
      )}
    </div>
  );
}
